// AI Upgraded Service
import { Injectable } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { AuditRepoService } from '@client/app/services/api/audit-repo/audit-repo.service';
import { AccessibilityCheckNameService } from '@client/app/modules/accessibility/services/accessibility-check-name/accessibility-check-name.service';
import { AuditlogModulesService } from '@client/app/services/auditlog-modules/auditlog-modules.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserDatePipe } from '@monsido/angular-shared-components';

type GetAuditLogsProgress = {
    current: number;
    total: number;
};

@Injectable({
    providedIn: 'root',
})
export class ExporterAuditLogParserService {
    private donGetData = false;
    getAuditLogsProgress$: BehaviorSubject<GetAuditLogsProgress | null> = new BehaviorSubject(null);

    get getAuditLogsProgressObs (): Observable<GetAuditLogsProgress | null> {
        return this.getAuditLogsProgress$.asObservable();
    }

    constructor (
        private readonly translateService: TranslateService,
        private readonly auditRepoService: AuditRepoService,
        private readonly accessibilityCheckNameService: AccessibilityCheckNameService,
        private readonly auditlogModulesService: AuditlogModulesService,
        private readonly userDatePipe: UserDatePipe,
    ) {}

    async getData (params: any, dataArray: any[]): Promise<any[]> {
        if (this.donGetData) {
            this.donGetData = false;
            return [];
        }

        await this.accessibilityCheckNameService.getAllChecks();

        params.page = params.page ? params.page + 1 : 1;
        const currentProgress = params.page ? (params.page - 1) * params.page_size : 0;
        this.setGetAuditLogsProgress(currentProgress);
        return this.auditRepoService.getAuditLogs(params).then((data) => {
            dataArray = dataArray.concat(data);
            if (data.length < params.page_size) {
                return this.parse(dataArray);
            }
            return this.getData(params, dataArray);
        });
    }

    stopGetData (): void {
        this.donGetData = true;
    }

    setGetAuditLogsProgress (current: number, total?: number): void {
        const newProgress = this.getAuditLogsProgress$.getValue() || { current: 0, total: 0 };
        if (current) {
            newProgress.current = current;
        }
        if (total) {
            newProgress.total = total;
        }

        this.getAuditLogsProgress$.next(newProgress);
    }

    clearGetAuditLogsProgress (): void {
        this.getAuditLogsProgress$.next(null);
    }

    private parseTargetURI (target) {
        const temp = target.substring(0, target.indexOf(':'));

        if (temp === 'PageCorrect') {
            return temp;
        }
        return temp.replace(/([A-Z])/g, ' $1').trim();
    }

    private parse (data: any[]): any[] {
        const csv: string[][] = [];
        const headlines = [
            this.translateService.getString('Date'),
            this.translateService.getString('By user'),
            this.translateService.getString('Domain'),
            this.translateService.getString('Module'),
            this.translateService.getString('Target'),
            this.translateService.getString('Identifier'),
            this.translateService.getString('Action'),
            this.translateService.getString('Comment'),
            this.translateService.getString('Page'),
            this.translateService.getString('Check'),
        ];
        csv.push(headlines);

        data.forEach((logItem) => {
            const entry = [
                this.userDatePipe.transform(logItem.created_at, ''),
                logItem.author,
                logItem.domain?.title ?? '',
                this.auditlogModulesService.getModuleNameFromValue(logItem.module),
                this.parseTargetURI(logItem.target_uri),
                logItem.data?.identifier ?? logItem.data?.source_code ?? '',
                logItem.action,
                logItem.data?.comment ?? '',
                logItem.data?.page ?? '',
                this.accessibilityCheckNameService.getNameSync(logItem.data?.check_id) ?? '',
            ];
            csv.push(entry);
        });

        return csv;
    }
}
